import React, { FunctionComponent, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import RegularButton from '../../../../components/RegularButton';
import { fromINameDataToICustomSelectData, openLeverAuthWindow } from './utils';
import { connect } from 'react-redux';
import { IAppState } from '../../../../common/models/store/IAppState';
import {
  disconnectFromLeverRoutine,
  getIntegrationsDataRoutine,
  setLeverStageRoutine,
  setLeverSurveyRoutine
} from '../../routines';
import { IWorkspaceIntegrations } from '../../../../common/models/workspace/IWorkspaceIntegrations';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import Icon from '../../../../components/Icon';
import { Integration } from '../../../../common/enums/Integration';
import CustomSelect from '../../../../components/CustomSelect';
import { INameData } from '../../../../common/models/common/INameData';
import Tooltip from '../../../../components/Tooltip';
import styles from './styles.module.scss';

interface IProps {
  isSaving: boolean;
  integrationsData?: IWorkspaceIntegrations;
  surveys: INameData[];
  leverStages: INameData[];
  setLeverStage: IBindingCallback1<string>;
  setLeverSurvey: IBindingCallback1<string>;
  getIntegrationsData: IBindingAction;
  disconnectFromLever: IBindingAction;
}

const IntegrationsPane: FunctionComponent<IProps> = ({
  surveys,
  isSaving,
  leverStages,
  integrationsData,
  setLeverStage,
  setLeverSurvey,
  getIntegrationsData,
  disconnectFromLever
}) => {
  useEffect(() => {
    if (!integrationsData) {
      getIntegrationsData();
    }
  }, []);

  return (
    <div className="position-relative flex-1 h-100">
      <LoaderWrapper loading={!integrationsData || isSaving} transparent={Boolean(integrationsData)}>
        <div className="toggler-container">
          <div className={styles.integrationBlockHeader}>
            <div className="settingsTitle mb-0">
              <FormattedMessage id="integrationsPane.Lever" defaultMessage="Lever integration" />
              {integrationsData?.[Integration.Lever] && (
                !integrationsData?.[Integration.Lever]?.defaultSurveyId
                || !integrationsData?.[Integration.Lever]?.stageId) && (
                <Tooltip
                  placement="top-start"
                  content={(
                    <FormattedMessage
                      id="leverWarning"
                      defaultMessage="You need to select default survey and Lever stage to complete the integration"
                    />
                  )}
                >
                  <Icon className="ml-3 cursor-pointer" icon="warning" />
                </Tooltip>
              )}
            </div>
            {integrationsData?.[Integration.Lever]
              ? (
                <RegularButton
                  textIntlId="disconnect"
                  defaultMessage="Disconnect"
                  variant="tertiary"
                  icon={<Icon icon="archive" />}
                  onClick={disconnectFromLever}
                />
              )
              : (
                <RegularButton
                  textIntlId="connect"
                  defaultMessage="Connect"
                  onClick={openLeverAuthWindow}
                  variant="secondary"
                />
              )}
          </div>
          {integrationsData?.[Integration.Lever] && (
            <div className={styles.leverSelectors}>
              <div className="d-flex align-items-center">
                <div className={styles.leverSelectorLabel}>
                  <FormattedMessage id="defaultSurvey" defaultMessage="Default survey" />
                </div>
                <CustomSelect
                  value={integrationsData[Integration.Lever]?.defaultSurveyId}
                  onChange={setLeverSurvey}
                  options={fromINameDataToICustomSelectData(surveys)}
                />
              </div>
              <div className="d-flex align-items-center">
                <div className={styles.leverSelectorLabel}>
                  <FormattedMessage id="leverStage" defaultMessage="Lever stage" />
                </div>
                <CustomSelect
                  value={integrationsData[Integration.Lever]?.stageId}
                  onChange={setLeverStage}
                  options={fromINameDataToICustomSelectData(leverStages)}
                />
              </div>
            </div>
          )}
        </div>
      </LoaderWrapper>
    </div>
  );
};

const mapStateToProps = ({
  settings: { integrations: { integrationsData, surveys, leverStages }, isSaving }
}: IAppState) => ({ integrationsData, isSaving, surveys, leverStages });

const mapDispachToProps = {
  getIntegrationsData: getIntegrationsDataRoutine,
  disconnectFromLever: disconnectFromLeverRoutine,
  setLeverStage: setLeverStageRoutine,
  setLeverSurvey: setLeverSurveyRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(IntegrationsPane);
